import {
	QueryFunctionContext,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from '@tanstack/react-query';
import {
	getCustomers,
	updateCustomer,
	deleteCustomer,
	createCustomer,
	getCustomer,
	getCustomerUsers,
	getCustomerSiteOptions,
	getCustomerDevices,
	getCustomerSites,
} from '@/api/customers';
import { UpdateCustomerRequest, CreateCustomerRequest } from '@/api/customers/types';
import useToastMessage from '@/shared/context/toast-message-context';
import { CreateMutationFnParams, UpdateMutationFnParams, UseMutationQueryParams } from './types';

const customerKeys = {
	allCustomers: () => ['all_customers'] as const,
	allCustomerUsers: () => ['all_customer_users'] as const,
	allCustomerSiteOptions: () => ['customer_site_options'] as const,
	allCustomerDevices: () => ['all_customer_devices'] as const,
	allCustomerSites: () => ['all_customer_sites'] as const,
	singleCustomer: () => ['customer'] as const,
};

interface UseCustomersParams {
	queryParams?: {
		limit?: number;
	};
}

export const useCustomers = ({ queryParams }: UseCustomersParams) => {
	const queryKey = [customerKeys.allCustomers(), queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<
			({ limit?: number } | readonly ['all_customers'] | undefined)[],
			number
		>) => getCustomers({ ...queryParams, page: pageParam }),
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
	});

	return { queryKey, query };
};

export const useCustomerSiteOptions = (customerId?: string) =>
	useQuery({
		queryKey: [...customerKeys.allCustomerSiteOptions(), customerId],
		queryFn: () => getCustomerSiteOptions(customerId!),
		enabled: !!customerId,
		refetchOnWindowFocus: false,
	});

export const useCustomer = (id?: string) => {
	const queryKey = [...customerKeys.singleCustomer(), id];
	const query = useQuery({
		queryKey,
		queryFn: () => getCustomer(id!),
		enabled: !!id,
		refetchOnWindowFocus: false,
	});

	return { queryKey, query };
};

interface UseCustomerUsersParams {
	customerId?: string;
	queryParams?: {
		limit?: number;
		search?: string;
		roleId?: string;
		siteId?: string;
	};
}

export const useCustomerUsers = ({ customerId, queryParams }: UseCustomerUsersParams) => {
	const queryKey = [...customerKeys.allCustomerUsers(), customerId, queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getCustomerUsers(customerId!, { ...queryParams, page: pageParam }),
		enabled: !!customerId,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
	});

	return { queryKey, query };
};

export interface CustomerDevicesQueryParams {
	siteId?: string;
	page?: number;
	limit?: number;
	search?: string;
}

export const useCustomerDevices = (
	customerId?: string,
	queryParams?: CustomerDevicesQueryParams
) => {
	const queryKey = [...customerKeys.allCustomerDevices(), customerId, queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getCustomerDevices(customerId!, { ...queryParams, page: pageParam }),
		enabled: !!customerId,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
	});

	return { queryKey, query };
};

export const useCustomerSites = () =>
	useQuery({
		queryKey: customerKeys.allCustomerSites(),
		queryFn: () => getCustomerSites(),
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

export const useCreateCustomer = (mutationQueryParams: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();
	return useMutation({
		mutationFn: ({ request }: CreateMutationFnParams<CreateCustomerRequest>) =>
			createCustomer(request),
		...mutationQueryParams,
		onSuccess: async () => {
			await mutationQueryParams?.onSuccess?.();
			dispatch({
				type: 'success',
				content: 'Customer successfully created',
			});
		},
	});
};

export const useUpdateCustomer = (mutationQueryParams: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();
	return useMutation({
		mutationFn: ({ id, request }: UpdateMutationFnParams<UpdateCustomerRequest>) =>
			updateCustomer(id, request),
		...mutationQueryParams,
		onSuccess: async () => {
			await mutationQueryParams?.onSuccess?.();
			dispatch({
				type: 'success',
				content: 'Customer successfully updated',
			});
		},
	});
};

export const useDeleteCustomer = (mutationQueryParams: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();
	return useMutation({
		mutationFn: (id: string) => deleteCustomer(id),
		...mutationQueryParams,
		onSuccess: async () => {
			await mutationQueryParams?.onSuccess?.();
			dispatch({
				type: 'success',
				content: 'Customer successfully deleted',
			});
		},
	});
};
